import React, { useEffect, useState } from "react";
import api, { fileUrl, asArray } from "../lib/api";
import { X, Globe2 } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";

// Continents with approximate "%" positions on the world map SVG (Equirectangular projection).
// Each entry lists the media outlets we want to surface — names must match brand-items DB rows.
const CONTINENTS = [
    {
        id: "americas",
        label_ar: "أمريكا",
        label_en: "Americas",
        // North-east of USA
        pos: { top: "36%", left: "22%" },
        outlets: ["CNN", "Wall Street Journal", "Forbes"],
    },
    {
        id: "europe",
        label_ar: "أوروبا",
        label_en: "Europe",
        // Central Europe
        pos: { top: "30%", left: "50%" },
        outlets: ["Bloomberg", "Sky News", "BBC News", "The Guardian", "Reuters"],
    },
    {
        id: "mena",
        label_ar: "الشرق الأوسط",
        label_en: "Middle East",
        // Saudi Arabia (Riyadh ~ 24.6°N, 46.7°E)
        pos: { top: "42%", left: "60%" },
        outlets: ["MBC Group", "Sky News Arabia", "Al Arabiya", "The National",
                  "Gulf News", "Arab News", "Asharq Al-Awsat"],
    },
    {
        id: "asia",
        label_ar: "آسيا",
        label_en: "Asia",
        // East Asia
        pos: { top: "40%", left: "78%" },
        outlets: ["China Daily", "The Japan Times", "Nikkei Asia", "Xinhua News Agency"],
    },
];

export default function WorldMapInteractive() {
    const { lang } = useI18n();
    const [media, setMedia] = useState([]);
    const [active, setActive] = useState(null);

    useEffect(() => {
        api.get("/brand-items?section=media").then((r) => setMedia(asArray(r.data, "items"))).catch(() => {});
    }, []);

    // Build a lookup for outlet logos by english name (fallback to AR)
    const findOutlet = (wanted) => {
        const w = wanted.toLowerCase().trim();
        return media.find((b) => {
            const en = (b.name_en || b.name || "").toLowerCase().trim();
            const ar = (b.name_ar || "").toLowerCase().trim();
            return en === w || ar === w;
        });
    };

    const activeContinent = active ? CONTINENTS.find((c) => c.id === active) : null;
    const activeOutlets = activeContinent
        ? activeContinent.outlets.map(findOutlet).filter(Boolean)
        : [];

    return (
        <div className="relative w-full" data-testid="world-map-interactive">
            <div className="relative aspect-[950/620] max-h-[560px] w-full mx-auto">
                <img
                    src="/world-map.svg"
                    alt="World map"
                    className="w-full h-full object-contain world-svg-tint"
                    data-testid="world-map-image"
                />
                {/* Continent hotspots */}
                {CONTINENTS.map((c) => (
                    <button
                        key={c.id}
                        onClick={() => setActive(c.id)}
                        style={c.pos}
                        className="absolute -translate-x-1/2 -translate-y-1/2 group"
                        data-testid={`continent-hotspot-${c.id}`}
                        aria-label={lang === "ar" ? c.label_ar : c.label_en}
                    >
                        <span className="relative flex h-5 w-5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0060A7] opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-5 w-5 bg-[#0060A7] ring-4 ring-[#0060A7]/30 group-hover:scale-150 transition-transform"></span>
                        </span>
                        <span className="absolute top-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] uppercase tracking-[0.2em] text-white/70 group-hover:text-white px-2 py-0.5 bg-[#002951]/80 backdrop-blur-sm">
                            {lang === "ar" ? c.label_ar : c.label_en}
                        </span>
                    </button>
                ))}
            </div>

            {/* Continent panel */}
            {activeContinent && (
                <div
                    className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
                    onClick={() => setActive(null)}
                    data-testid="continent-panel-overlay"
                >
                    <div
                        className="bg-[#002951] text-white max-w-4xl w-full p-8 md:p-12 relative animate-slide-up max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`continent-panel-${activeContinent.id}`}
                    >
                        <button
                            onClick={() => setActive(null)}
                            className="absolute top-5 right-5 p-2 hover:bg-white/10 rounded-full"
                            aria-label="close"
                            data-testid="continent-panel-close"
                        >
                            <X size={20} />
                        </button>
                        <div className="flex items-center gap-3 mb-2">
                            <Globe2 size={18} className="text-[#0060A7]" />
                            <span className="text-xs uppercase tracking-[0.3em] text-white/50">
                                {lang === "ar" ? "من أبرز حضورنا في" : "Featured presence in"}
                            </span>
                        </div>
                        <h3 className="heading text-3xl md:text-5xl tracking-tight font-black mb-2">
                            {lang === "ar" ? activeContinent.label_ar : activeContinent.label_en}
                        </h3>
                        <p className="text-white/60 text-sm mb-10">
                            {lang === "ar"
                                ? "أبرز المنصات الإعلامية التي عملنا معها في هذه القارة."
                                : "Featured media outlets we have worked with across the region."}
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {activeOutlets.length === 0 ? (
                                <div className="col-span-full text-white/40 text-sm">
                                    {lang === "ar" ? "جارٍ التحميل…" : "Loading…"}
                                </div>
                            ) : (
                                activeOutlets.map((o) => (
                                    <div
                                        key={o.id}
                                        className="bg-white aspect-[5/3] flex items-center justify-center p-5 hover:scale-105 transition-transform"
                                        data-testid={`outlet-${o.id}`}
                                    >
                                        {o.image ? (
                                            <img
                                                src={fileUrl(o.image)}
                                                alt={o.name_en || o.name_ar}
                                                loading="lazy"
                                                decoding="async"
                                                className="max-w-full max-h-full object-contain"
                                            />
                                        ) : (
                                            <span className="text-[#002951] font-bold text-sm text-center">
                                                {o.name_en || o.name_ar}
                                            </span>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
